import { computed, reactive, ref, watch } from 'vue'
import { type Ref } from 'vue'
import { type CustomerCase as Case, DocumentsService, type Document } from '@/types/generated'
import { emitter } from './eventEmitter'
import { genericErrorToast } from '@/lib/toasts'
import { debounce } from 'lodash'

// Simplified Document processing state interface
export interface DocumentProcessState {
  isProcessing: boolean
  isAiReady: boolean
  isAiComplete: boolean
  hasError: boolean
  pollingActive: boolean
  hasRaptorStillProcessing: boolean
}

// Document status event interface
export interface DocumentStatusEvent {
  documentUuid: string
  status: string
  isComplete: boolean
  errorMessage?: string
}

// Interface for uploads in progress
export interface ActiveUpload {
  id: string
  filename: string
  size: number
  progress: number
  caseUuid: string
  started: number
  documentUuid?: string
  error?: string
}

// Create a document processing module that can be composed into the main store
export function useDocumentModule(caseRecord: Ref<Case | null>) {
  // Document processing state tracking
  const documentProcessingStates = reactive<Record<string, DocumentProcessState>>({})
  const pollingIntervals = reactive<Record<string, number>>({})
  const pendingProcessingDocuments = reactive<
    Record<string, { timestamp: number; document: Document }>
  >({})

  // Add upload tracking to the same module
  const activeUploads = reactive<Record<string, ActiveUpload>>({})

  function isDocumentProcessing(doc: Document): boolean {
    // First check if the document has critical errors - if it does, it's not processing
    if (hasDocumentCriticalError(doc)) {
      return false
    }

    // Check if document needs metadata enrichment and it's not finished
    // Also check the timestamps directly as a fallback
    return (
      (doc.needs_metadata_enrichment && !doc.metadata_enrichment_finished_at) ||
      (doc.metadata_enrichment_started_at && !doc.metadata_enrichment_finished_at)
    )
  }

  function isDocumentAiReady(doc: Document): boolean {
    // Document is AI ready if metadata enrichment is finished and there are no critical errors
    // Note: Raptor summaries may still be processing in the background, but that's ok
    return Boolean(
      doc.metadata_enrichment_finished_at &&
        doc.metadata_enrichment_is_finished &&
        !hasDocumentCriticalError(doc)
    )
  }

  function isDocumentAiComplete(doc: Document): boolean {
    // Document is AI complete if metadata enrichment is finished
    // Note: Raptor summaries may still be processing in the background, but that's ok for AI usage

    // If the document has critical errors, it's not AI complete
    if (hasDocumentCriticalError(doc)) {
      return false
    }

    return Boolean(doc.metadata_enrichment_finished_at && doc.metadata_enrichment_is_finished)
  }

  function hasDocumentCriticalError(doc: Document): boolean {
    // Simplification: A document has a critical error ONLY if metadata_enrichment_error is filled
    // This is the centralized error indicator from the backend
    return Boolean(doc.metadata_enrichment_error && doc.metadata_enrichment_error.length > 0)
  }

  function hasRaptorStillProcessing(doc: Document): boolean {
    // Check if Raptor summaries are still being generated
    // This is informational only and doesn't affect document usability
    return Boolean(
      doc.needs_raptor_summaries &&
        doc.raptor_summaries_started_at &&
        !doc.raptor_summaries_finished_at
    )
  }

  function addToPendingProcessing(documentUuid: string, document: Document) {
    pendingProcessingDocuments[documentUuid] = {
      timestamp: Date.now(),
      document
    }

    // Don't remove from pending processing for at least 20 seconds
    // This ensures the document remains visible during state transitions
    setTimeout(
      () => {
        if (pendingProcessingDocuments[documentUuid]) {
          // Only remove if the document is visible in some other collection
          if (caseRecord.value?.documents.some((d) => d.uuid === documentUuid)) {
            delete pendingProcessingDocuments[documentUuid]
          } else {
            // If not in case documents yet, check again later
            setTimeout(() => {
              if (pendingProcessingDocuments[documentUuid]) {
                delete pendingProcessingDocuments[documentUuid]
              }
            }, 30000) // Additional 30 seconds
          }
        }
      },
      20000 // 20 seconds initial delay
    )
  }

  function initDocumentTracking(doc: Document) {
    if (!doc.uuid) return

    // Initialize the processing state with simplified properties
    documentProcessingStates[doc.uuid] = {
      isProcessing: isDocumentProcessing(doc),
      isAiReady: isDocumentAiReady(doc),
      isAiComplete: isDocumentAiComplete(doc),
      hasError: hasDocumentCriticalError(doc),
      pollingActive: false,
      hasRaptorStillProcessing: hasRaptorStillProcessing(doc)
    }

    // TODO: We don't want to automatically poll for documents on page refresh
    // This can lead to flaky behavior and unexpected state changes
    // Polling should only be started for newly uploaded documents or when explicitly requested
    /*
    // Start polling if document is still processing
    if (documentProcessingStates[doc.uuid].isProcessing) {
      startDocumentPolling(doc.uuid)
    }
    */
  }

  // Document polling logic
  function startDocumentPolling(documentUuid: string): void {
    // Skip if already polling
    if (pollingIntervals[documentUuid]) return
    // Mark as polling
    if (documentProcessingStates[documentUuid]) {
      documentProcessingStates[documentUuid].pollingActive = true
    }

    // Set up interval for polling
    pollingIntervals[documentUuid] = window.setInterval(async () => {
      try {
        await pollDocumentStatus(documentUuid)
      } catch (error) {
        console.error(`Error polling document status for ${documentUuid}:`, error)
        stopDocumentPolling(documentUuid)
      }
    }, 5000) // Poll every 5 seconds (increased from 2s to reduce flickering)
  }

  async function pollDocumentStatus(documentUuid: string): Promise<void> {
    const doc = await DocumentsService.documentsRetrieve({
      uuid: documentUuid
    })

    if (!doc) return

    if (pendingProcessingDocuments[documentUuid]) {
      // If document is no longer processing, remove from pending
      if (!isDocumentProcessing(doc)) {
        delete pendingProcessingDocuments[documentUuid]
      }
    }

    const state = documentProcessingStates[documentUuid]
    if (!state) return

    // Save previous state for comparison
    const prevState = { ...state }

    // Update processing state
    state.isProcessing = isDocumentProcessing(doc)

    // Check for error state first
    const hasError = hasDocumentCriticalError(doc)
    const errorChanged = prevState.hasError !== hasError
    state.hasError = hasError

    // Update AI ready state
    state.isAiReady = isDocumentAiReady(doc)

    // Update AI complete state
    state.isAiComplete = isDocumentAiComplete(doc)

    // Update Raptor still processing state
    state.hasRaptorStillProcessing = hasRaptorStillProcessing(doc)

    // Emit events on state changes

    // Emit error event if error state changed to true
    if (!prevState.hasError && state.hasError) {
      console.log(`Document ${documentUuid} encountered an error, emitting event`)
      emitter.emit('document-error', {
        documentUuid,
        status: 'error',
        isComplete: true,
        errorMessage: 'AI analysis failed\nPlease upload another file'
      } as DocumentStatusEvent)
    }

    if (!prevState.isAiReady && state.isAiReady) {
      emitter.emit('document-ai-ready', {
        documentUuid,
        status: 'aiReady',
        isComplete: false
      } as DocumentStatusEvent)
    }

    if (!prevState.isAiComplete && state.isAiComplete) {
      emitter.emit('document-ai-complete', {
        documentUuid,
        status: 'aiComplete',
        isComplete: true
      } as DocumentStatusEvent)
    }

    // Update document in case record if it exists
    if (caseRecord.value?.documents) {
      const docIndex = caseRecord.value.documents.findIndex((d) => d.uuid === documentUuid)
      if (docIndex !== -1) {
        caseRecord.value.documents[docIndex] = doc
      }
    }

    // Stop polling if processing is complete OR if there's an error
    if (!state.isProcessing) {
      trackDocument(documentUuid)
      stopDocumentPolling(documentUuid)
    }
  }

  function stopDocumentPolling(documentUuid: string): void {
    if (pollingIntervals[documentUuid]) {
      clearInterval(pollingIntervals[documentUuid])
      delete pollingIntervals[documentUuid]

      if (documentProcessingStates[documentUuid]) {
        documentProcessingStates[documentUuid].pollingActive = false
      }
    }
  }

  // Function to manually track a document (for newly uploaded documents)
  function trackDocument(documentUuid: string): void {
    // Skip tracking if already in pending processing (to avoid state resets)
    if (pendingProcessingDocuments[documentUuid]) {
      return
    }

    DocumentsService.documentsRetrieve({ uuid: documentUuid })
      .then((doc) => {
        // Complete any uploads associated with this document
        Object.values(activeUploads).forEach((upload) => {
          if (upload.documentUuid === documentUuid) {
            // Just update the progress to 100% but don't remove the upload
            // until we're sure the document is tracked in processing
            upload.progress = 100

            // Only complete (and remove) the upload if we're already tracking it
            // as a processing document
            if (documentProcessingStates[documentUuid]?.isProcessing) {
              completeUpload(upload.id, documentUuid, doc)
            }
          }
        })

        // Initialize or reinitialize tracking
        if (documentProcessingStates[documentUuid]) {
          // Update existing tracking state
          documentProcessingStates[documentUuid].isProcessing = isDocumentProcessing(doc)
          documentProcessingStates[documentUuid].isAiReady = isDocumentAiReady(doc)
          documentProcessingStates[documentUuid].isAiComplete = isDocumentAiComplete(doc)
          documentProcessingStates[documentUuid].hasError = hasDocumentCriticalError(doc)
          documentProcessingStates[documentUuid].hasRaptorStillProcessing =
            hasRaptorStillProcessing(doc)
        } else {
          // Initialize new tracking
          initDocumentTracking(doc)

          // If this is a newly tracked document that's processing,
          // keep it in pendingProcessingDocuments temporarily
          if (isDocumentProcessing(doc)) {
            addToPendingProcessing(documentUuid, doc)
          }
        }

        // Start polling if needed but not already active
        if (
          documentProcessingStates[documentUuid]?.isProcessing &&
          !documentProcessingStates[documentUuid]?.pollingActive
        ) {
          startDocumentPolling(documentUuid)
        }
      })
      .catch((error) => {
        console.error(`Failed to retrieve document ${documentUuid}:`, error)
      })
  }

  // Clean up function to stop all polling
  function stopAllDocumentPolling(): void {
    Object.keys(pollingIntervals).forEach((uuid) => {
      clearInterval(pollingIntervals[uuid])
      delete pollingIntervals[uuid]
    })

    // Update all tracking states
    Object.keys(documentProcessingStates).forEach((uuid) => {
      documentProcessingStates[uuid].pollingActive = false
    })
  }

  // Initialize tracking for all case documents
  function initAllDocumentsTracking(): void {
    if (caseRecord.value?.documents) {
      caseRecord.value.documents.forEach((doc) => {
        if (doc.uuid && !documentProcessingStates[doc.uuid]) {
          initDocumentTracking(doc)
        }
      })
    }
  }

  // Clear all document processing states
  function clearDocumentProcessingStates(): void {
    Object.keys(documentProcessingStates).forEach((key) => {
      delete documentProcessingStates[key]
    })
  }

  // Register a new upload
  function registerUpload(caseUuid: string, filename: string, size: number): string {
    const id = `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

    activeUploads[id] = {
      id,
      filename,
      size,
      progress: 0,
      caseUuid,
      started: Date.now()
    }

    return id
  }

  // Update upload progress
  function updateUploadProgress(id: string, progress: number): void {
    const upload = activeUploads[id]
    if (upload) {
      upload.progress = progress
    }
  }

  // Set error for an upload
  function setUploadError(id: string, error: string): void {
    const upload = activeUploads[id]
    if (upload) {
      upload.error = error
    }
    delete activeUploads[id]
    genericErrorToast()
  }

  // Complete an upload and link it to a document
  function completeUpload(id: string, documentUuid: string, document?: Document): void {
    const upload = activeUploads[id]
    if (!upload) return

    // Update upload with document UUID and 100% progress
    upload.documentUuid = documentUuid
    upload.progress = 100

    // Initialize tracking for the document if provided
    if (document && document.uuid) {
      // Add to pending processing - this will keep the document tracked
      // even if it disappears briefly from other collections
      addToPendingProcessing(document.uuid, document)

      // Initialize tracking if not already tracking
      if (!documentProcessingStates[document.uuid]) {
        initDocumentTracking(document)
      }

      // IMPORTANT: Don't remove the upload until we're sure the document
      // is visible in the UI via another collection

      // First delay gives time for the document to appear in processing collection
      setTimeout(() => {
        // Only remove if document is definitely being shown in processing
        if (document.uuid && documentProcessingStates[document.uuid]?.isProcessing) {
          removeUpload(id)
        } else {
          // If still not shown as processing, wait longer
          setTimeout(() => {
            removeUpload(id)
          }, 2000)
        }
      }, 2000)
    } else {
      // No document provided - keep upload visible for a bit longer
      setTimeout(() => {
        removeUpload(id)
      }, 2000)
    }
  }

  // Remove an upload from tracking
  function removeUpload(id: string): void {
    delete activeUploads[id]
  }

  // Get uploads for a specific case
  function getUploadsForCase(caseUuid: string): ActiveUpload[] {
    const res = Object.values(activeUploads).filter((upload) => upload.caseUuid === caseUuid)
    return res
  }

  // Clear all uploads for a case
  function clearUploadsForCase(caseUuid: string): void {
    Object.values(activeUploads).forEach((upload) => {
      if (upload.caseUuid === caseUuid) {
        delete activeUploads[upload.id]
      }
    })
  }

  // Computed property for processing documents (only those truly processing, not those with non-critical errors)
  const processingDocuments = computed(() => {
    if (!caseRecord.value?.documents) return []
    return caseRecord.value.documents.filter((doc) => {
      // Only include documents that are actively processing (not finished, not with critical error)
      return isDocumentProcessing(doc)
    })
  })

  // Computed property for documents with critical errors (not Raptor-only errors)
  const documentsWithCriticalErrors = computed(() => {
    if (!caseRecord.value?.documents) return []
    return caseRecord.value.documents.filter((doc) => hasDocumentCriticalError(doc))
  })

  // Computed property for number of documents processing (including uploads)
  const processingDocumentsCount = computed(() => {
    const processingCount = processingDocuments.value.length

    // Filter uploads to avoid double-counting documents that are both in uploads and processing
    const uploadsWithoutDocumentUuid = caseRecord.value
      ? getUploadsForCase(caseRecord.value.uuid || '').filter((upload) => {
          // Only count uploads that don't have a documentUuid or whose documentUuid isn't already in processing
          return (
            !upload.documentUuid ||
            !processingDocuments.value.some((doc) => doc.uuid === upload.documentUuid)
          )
        })
      : []

    return processingCount + uploadsWithoutDocumentUuid.length
  })

  // Computed property to check if any documents are processing
  const hasProcessingDocuments = computed(() => {
    return processingDocumentsCount.value > 0
  })

  // Computed property to get the active uploads
  const activeUploadsArray = computed(() => {
    return Object.values(activeUploads)
  })

  // Computed property to check if any uploads are in progress
  const hasActiveUploads = computed(() => {
    return activeUploadsArray.value.length > 0
  })

  // Computed property for AI ready documents
  const aiReadyDocuments = computed(() => {
    return (
      caseRecord.value?.documents?.filter((doc) => {
        return (
          doc.uuid &&
          documentProcessingStates[doc.uuid]?.isAiReady &&
          !documentProcessingStates[doc.uuid]?.isAiComplete
        )
      }) || []
    )
  })

  // Computed property for AI complete documents
  const aiCompleteDocuments = computed(() => {
    return (
      caseRecord.value?.documents?.filter((doc) => {
        return doc.uuid && documentProcessingStates[doc.uuid]?.isAiComplete
      }) || []
    )
  })

  // Computed property for documents with errors
  const documentsWithErrors = computed(() => {
    return (
      caseRecord.value?.documents?.filter((doc) => {
        return doc.uuid && documentProcessingStates[doc.uuid]?.hasError
      }) || []
    )
  })

  // Computed property for documents with Raptor still processing
  const documentsWithRaptorStillProcessing = computed(() => {
    return (
      caseRecord.value?.documents?.filter((doc) => {
        return doc.uuid && documentProcessingStates[doc.uuid]?.hasRaptorStillProcessing
      }) || []
    )
  })

  // Function to check if document is usable by AI (regardless of non-critical errors)
  function isDocumentUsableForAi(doc: Document): boolean {
    // A document is usable for AI if it has completed metadata enrichment without critical errors
    // Critical errors are only OCR and initial embeddings failures
    // Non-critical errors (Raptor, summary, analysis) don't prevent AI usability
    return Boolean(
      doc.metadata_enrichment_finished_at &&
        doc.metadata_enrichment_is_finished &&
        !hasDocumentCriticalError(doc)
    )
  }

  // Get document processing state
  function getDocumentProcessingState(documentUuid: string): DocumentProcessState | undefined {
    return documentProcessingStates[documentUuid]
  }

  // Check if document is in specific state
  function isDocumentInProcess(documentUuid: string): boolean {
    const inProcessingState = Boolean(documentProcessingStates[documentUuid]?.isProcessing)
    const inPendingProcessing = Boolean(pendingProcessingDocuments[documentUuid])
    const inProcessingDocuments = processingDocuments.value.some((doc) => doc.uuid === documentUuid)

    return inProcessingState || inPendingProcessing || inProcessingDocuments
  }

  function isDocumentAiReadyState(documentUuid: string): boolean {
    return Boolean(documentProcessingStates[documentUuid]?.isAiReady)
  }

  function isDocumentAiCompleteState(documentUuid: string): boolean {
    return Boolean(documentProcessingStates[documentUuid]?.isAiComplete)
  }

  function isDocumentRaptorStillProcessingState(documentUuid: string): boolean {
    return Boolean(documentProcessingStates[documentUuid]?.hasRaptorStillProcessing)
  }

  // Function to refresh a document's data from the server
  async function refreshDocumentData(documentUuid: string): Promise<Document | null> {
    if (!documentUuid || !caseRecord.value?.uuid) return null

    try {
      console.log('Refreshing document data for:', documentUuid)
      const response = await DocumentsService.documentsRetrieve({
        caseUuid: caseRecord.value.uuid,
        uuid: documentUuid
      })

      console.log('Refreshed document data:', response)

      // Update the document in the case record
      if (caseRecord.value?.documents) {
        const index = caseRecord.value.documents.findIndex((d) => d.uuid === documentUuid)
        if (index >= 0) {
          caseRecord.value.documents[index] = response
          console.log('Updated document in case record')
        }
      }

      return response
    } catch (error) {
      console.error('Error refreshing document:', error)
      return null
    }
  }

  // Function to check for critical errors by UUID with refresh
  async function hasDocumentCriticalErrorByUuidWithRefresh(documentUuid: string): Promise<boolean> {
    if (!caseRecord.value || !documentUuid) return false

    // Refresh the document data first
    const refreshedDoc = await refreshDocumentData(documentUuid)

    // Use the refreshed document if available, otherwise fall back to the stored one
    const doc = refreshedDoc || caseRecord.value.documents?.find((d) => d.uuid === documentUuid)
    if (!doc) return false

    // Debug logging
    console.log('Checking document for critical errors (with refresh):', documentUuid)
    console.log('Document OCR error:', doc.ocr_error)
    console.log('Document embeddings error:', doc.embeddings_error)

    const hasError = hasDocumentCriticalError(doc)
    console.log('Has critical error:', hasError)

    return hasError
  }

  // Function to check for critical errors by UUID
  function hasDocumentCriticalErrorByUuid(documentUuid: string): boolean {
    if (!caseRecord.value || !documentUuid) return false

    const doc = caseRecord.value.documents?.find((d) => d.uuid === documentUuid)
    if (!doc) return false

    // Debug logging
    console.log('Checking document for critical errors:', documentUuid)
    console.log('Document OCR error:', doc.ocr_error)
    console.log('Document embeddings error:', doc.embeddings_error)

    const hasError = hasDocumentCriticalError(doc)
    console.log('Has critical error:', hasError)

    return hasError
  }

  // Watch for changes in the documents array
  watch(
    () => caseRecord.value?.documents,
    (newDocs) => {
      if (newDocs) {
        // Find documents that weren't in the old array
        const oldDocIds = new Set((newDocs || []).map((doc) => doc.uuid).filter(Boolean))

        // Check each document in the new array
        newDocs.forEach((doc) => {
          if (doc.uuid && !oldDocIds.has(doc.uuid) && !documentProcessingStates[doc.uuid]) {
            // This is a new document - start tracking it
            initDocumentTracking(doc)
          }
        })
      }
    },
    { deep: true }
  )

  const displayProcessingCount = computed(() => processingDocumentsCount.value)

  // Create a debounced update function
  const updateDisplayCount = debounce((newCount) => {
    // Only decrease the count after a delay if it would drop to zero
    if (newCount === 0 && displayProcessingCount.value > 0) {
      // Check if the count might increase again soon before actually setting to zero
      setTimeout(() => {
        // Re-check the current count before finalizing
        const currentCount = processingDocumentsCount.value
        displayProcessingCount.value = currentCount
      }, 300)
    } else {
      // For non-zero or increases, update immediately
      displayProcessingCount.value = newCount
    }
  }, 300) // Small delay to smooth out rapid changes

  // Watch the raw count for changes
  watch(processingDocumentsCount, (newCount) => {
    // Simple rule: never decrease from a positive number to zero immediately
    if (newCount === 0 && displayProcessingCount.value > 0) {
      updateDisplayCount(newCount)
    } else {
      // For other changes, particularly increases, update more quickly
      displayProcessingCount.value = newCount
    }
  })

  return {
    // Document Computed Properties
    processingDocuments,
    processingDocumentsCount,
    hasProcessingDocuments,
    aiReadyDocuments,
    aiCompleteDocuments,
    pendingProcessingDocuments,
    documentsWithErrors,
    documentsWithRaptorStillProcessing,
    documentsWithCriticalErrors,

    // Document State Management
    initAllDocumentsTracking,
    clearDocumentProcessingStates,

    // Document Tracking Functions
    trackDocument,
    stopDocumentPolling,
    stopAllDocumentPolling,
    getDocumentProcessingState,
    isDocumentInProcess,
    isDocumentAiReadyState,
    isDocumentAiCompleteState,
    isDocumentRaptorStillProcessingState,
    hasDocumentCriticalErrorByUuid,
    hasDocumentCriticalErrorByUuidWithRefresh,
    refreshDocumentData,

    // Upload Management Functions
    registerUpload,
    updateUploadProgress,
    setUploadError,
    completeUpload,
    removeUpload,
    getUploadsForCase,
    clearUploadsForCase,
    activeUploadsArray,
    hasActiveUploads,
    displayProcessingCount
  }
}
